<script setup lang="ts">
  import { computed, ref, useListeners } from 'vue';
  import { onLongPress } from '@vueuse/core';
  import { IPosTableView } from '@/interfaces/IPosTableView';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { displayPrice } from '@/utils';
  import { isPosTableOccupied } from '@/domain/reservations/pos-table-hints';

  const props = defineProps<{
    table: IPosTableView;
    /** Fixed-size card for the "Sin ubicar" strip (not absolutely placed). */
    compact?: boolean;
  }>();

  const emit = defineEmits<{
    (e: 'click'): void;
    /** Secondary action: touch long-press or right-click (POS V2 preview). */
    (e: 'longpress'): void;
  }>();

  const restaurantStore = useRestaurantStore();
  const listeners = useListeners();

  const occupied = computed(() => isPosTableOccupied(props.table));
  const seated = computed(() => props.table.reservation?.status === 'seated');
  const hasNext = computed(
    () =>
      props.table.tableStatus !== 'empty' &&
      !!props.table.reservation &&
      !seated.value
  );
  const reservationLabelKey = computed(() =>
    seated.value
      ? 'reservations.pos.tileSeated'
      : hasNext.value
      ? 'reservations.pos.tileNext'
      : 'reservations.pos.tileReserved'
  );

  const totalLabel = computed(() => {
    const symbol = restaurantStore.currency?.symbol ?? '';
    return `${symbol} ${displayPrice(props.table.total)}`;
  });

  //#region Long-press / right-click secondary action

  const rootRef = ref<HTMLElement>();

  // One-shot per press: dedupes onLongPress + contextmenu (Android fires the
  // latter on touch long-press, Windows on right-button release) and swallows
  // the click that follows a long-press release, no matter how long the hold
  // lasted. Reset on the next press so a stale flag can never eat a click.
  let longPressFired = false;

  function handlePointerDown() {
    longPressFired = false;
  }

  function triggerLongPress() {
    if (longPressFired) return;
    longPressFired = true;
    emit('longpress');
  }

  // Only arm the machinery when a consumer listens: in the old POS a slow
  // press must keep behaving as a plain click.
  if (listeners.longpress) {
    onLongPress(rootRef, triggerLongPress, { delay: 500 });
  }

  function handleClick() {
    if (longPressFired) {
      longPressFired = false;
      return;
    }
    emit('click');
  }

  function handleContextMenu(event: MouseEvent) {
    // Only hijack the context menu when a consumer listens (old POS keeps
    // the browser default).
    if (!listeners.longpress) return;
    event.preventDefault();
    triggerLongPress();
  }

  //#endregion
</script>

<template>
  <div
    ref="rootRef"
    class="pos-floor-table-tile"
    :class="[
      `tile--${table.tableStatus}`,
      {
        'tile--round': table.shape === 'round',
        'tile--compact': compact,
        'tile--seated': table.tableStatus === 'empty' && seated,
        'tile--reserved':
          table.tableStatus === 'empty' && !seated && table.reservation?.soon,
        'tile--late': table.reservation?.late,
        'tile--has-next': hasNext,
      },
    ]"
    @pointerdown="handlePointerDown"
    @click="handleClick"
    @contextmenu="handleContextMenu"
  >
    <div class="tile-inner">
      <span class="tile-name">{{ table.name }}</span>
      <template v-if="occupied">
        <span class="tile-count">
          <v-icon class="count-icon">mdi-receipt</v-icon>
          {{ table.orders.length }}
        </span>
        <span class="tile-total">{{ totalLabel }}</span>
      </template>
      <span v-if="table.reservation" class="tile-reservation">
        <v-icon class="reservation-icon">mdi-clock-outline</v-icon>
        {{ $t(reservationLabelKey, { time: table.reservation.timeLabel }) }}
        <span v-if="!compact" class="reservation-party">
          · {{ table.reservation.partySize }}</span
        >
      </span>
    </div>
  </div>
</template>

<style scoped lang="scss">
  // Flat status language: white = free, amber = in progress, sky = to
  // collect, emerald = paid. Status borders + tints, no gradients.
  .pos-floor-table-tile {
    @apply tw-cursor-pointer tw-select-none;

    &.tile--compact {
      @apply tw-w-28 tw-h-[4.25rem] tw-shrink-0;
    }

    .tile-inner {
      // Individual offsets + margin gap instead of `inset` / flex `gap`:
      // both are unsupported on old POS devices (Chrome <84).
      @apply tw-absolute tw-top-0.5 tw-right-0.5 tw-bottom-0.5 tw-left-0.5 tw-rounded-lg tw-bg-white;
      @apply tw-flex tw-flex-col tw-items-center tw-justify-center tw-overflow-hidden;
      @apply tw-px-1 tw-text-center;
      @apply tw-border-2 tw-border-neutral-200 tw-text-neutral-500;
      transition: background-color 0.15s ease, border-color 0.15s ease;

      .tile-name {
        @apply tw-max-w-full tw-truncate tw-text-[13px] tw-font-bold tw-leading-tight;
      }

      .tile-count {
        @apply tw-inline-flex tw-items-center tw-leading-tight tw-opacity-80;
        font-size: 0.65rem;

        .count-icon {
          @apply tw-mr-0.5;
          font-size: 0.7rem;
          color: currentColor !important;
        }
      }

      .tile-total {
        @apply tw-max-w-full tw-truncate tw-font-semibold tw-tabular-nums tw-leading-tight;
        font-size: 0.65rem;
      }
    }

    &.tile--compact .tile-inner {
      @apply tw-relative tw-top-0 tw-right-0 tw-bottom-0 tw-left-0 tw-h-full;
    }

    &.tile--round .tile-inner {
      @apply tw-rounded-full;
    }

    &.tile--pending .tile-inner,
    &.tile--seated .tile-inner {
      @apply tw-bg-amber-100 tw-border-amber-400 tw-text-amber-900;
    }

    &.tile--finished .tile-inner {
      @apply tw-bg-sky-100 tw-border-sky-400 tw-text-sky-900;
    }

    &.tile--paid .tile-inner {
      @apply tw-bg-emerald-100 tw-border-emerald-400 tw-text-emerald-900;
    }

    &.tile--reserved .tile-inner {
      @apply tw-border-dashed tw-border-sky-400 tw-bg-sky-50 tw-text-sky-900;
    }
    .tile-reservation {
      @apply tw-mt-0.5 tw-flex tw-max-w-full tw-items-center tw-truncate tw-text-[10px] tw-font-medium tw-leading-tight tw-text-sky-700;
      .reservation-icon {
        @apply tw-mr-0.5 tw-text-[11px] tw-text-current;
      }
      .reservation-party {
        @apply tw-whitespace-pre;
      }
    }
    &.tile--seated .tile-reservation {
      @apply tw-text-amber-800;
    }
    &.tile--has-next .tile-reservation {
      @apply tw-absolute tw-right-1 tw-top-1 tw-mt-0 tw-rounded tw-bg-sky-100 tw-px-1 tw-py-0.5;
    }
    &.tile--has-next .tile-inner {
      @apply tw-pt-3;
    }

    &:hover {
      .tile-inner {
        @apply tw-bg-neutral-50 tw-border-neutral-300;
      }

      &.tile--pending .tile-inner,
      &.tile--seated .tile-inner {
        @apply tw-bg-amber-200 tw-border-amber-500;
      }

      &.tile--finished .tile-inner {
        @apply tw-bg-sky-200 tw-border-sky-500;
      }

      &.tile--paid .tile-inner {
        @apply tw-bg-emerald-200 tw-border-emerald-500;
      }
      &.tile--reserved .tile-inner {
        @apply tw-border-sky-500 tw-bg-sky-100;
      }
    }
    &.tile--late .tile-inner {
      @apply tw-border-red-400;
    }
    &.tile--late .tile-reservation {
      @apply tw-text-red-700;
    }
  }
</style>
